import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle2, Trash2, Undo2, Circle, RotateCcw, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { haptics } from '@/lib/haptics'
import { toast } from 'sonner'
import { ContextMenu, ContextMenuTrigger, ContextMenuItem } from '@/components/ui/context-menu'
import { store, actions, undo, redo, createTodo, createSubtask } from '@/store'
import { showUndoToast } from '@/lib/toast-utils'
import { GlobalContextMenuContent } from './global-context-menu'
import { OverlaysSection } from './overlays-section'
import { CommandCenterSection } from './command-center-section'
import { TaskViewSection } from './task-view-section'
import { easeOutQuint } from './motion-presets'
import { Kbd, KbdGroup } from '@/components/ui/kbd'

// Hook types

import { useSounds } from '@/hooks/use-sounds'
// Re-declare local types or imports
const uiVisibilityKeys = [
  'showTime',
  'showWeather',
  'showSidebar',
  'showStats',
  'showSearch',
  'showViewFilter',
  'showCommandCenter',
  'showShortcuts',
  'showTheme',
]
const isUiVisibilityKey = (key) => uiVisibilityKeys.includes(key)

const editableContentSelector = '[contenteditable]:not([contenteditable="false"])'
const shortcutBlockSelectors = [
  'button',
  '[role="button"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="dialog"]',
  '[data-radix-portal]',
  '[data-radix-popper-content-wrapper]',
  '[data-add-task]',
]

const hasClosestMatch = (target, selectors) => {
  if (!(target instanceof Element)) return false
  return selectors.some((selector) => target.closest(selector))
}

const isEditableTarget = (target) =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  hasClosestMatch(target, [editableContentSelector])

const isShortcutBlockedTarget = (target) => hasClosestMatch(target, shortcutBlockSelectors)

const isPrimaryPointerEvent = (event) => {
  if (!event) return false
  if (event.pointerType === 'touch') return true
  if (typeof event.button === 'number') return event.button === 0
  return true
}
const isInsideAddTask = (target) =>
  target instanceof Element && Boolean(target.closest('[data-add-task]'))
const isAddTaskFocused = () =>
  typeof document !== 'undefined' &&
  document.activeElement instanceof Element &&
  Boolean(document.activeElement.closest('[data-add-task]'))

const dragBlockSelectors = [
  '#sidebar-container',
  '.group\\/resize',
  '.resize-handle',
  '[data-drag-handle]',
  '[data-add-task]',
]
const interactiveSelectors = [
  'button',
  'input',
  'textarea',
  '[role="menuitem"]',
  '[role="combobox"]',
  '[role="option"]',
  '[role="dialog"]',
  '[role="popover"]',
  '[data-radix-portal]',
  '[data-radix-popper-content-wrapper]',
  '.group\\/task-actions',
  '.task-item',
]
const isDragBlockedTarget = (target, isResizing, isDragActive) =>
  isResizing || isDragActive || hasClosestMatch(target, dragBlockSelectors)
const isInteractiveTarget = (target) => hasClosestMatch(target, interactiveSelectors)
const viewHintMap = new Map([
  [
    'list',
    [
      {
        key: '2',
        label: 'Calendar',
      },
      {
        key: '3',
        label: 'Kanban',
      },
    ],
  ],
  [
    'calendar',
    [
      {
        key: '1',
        label: 'List',
      },
      {
        key: '3',
        label: 'Kanban',
      },
    ],
  ],
  [
    'kanban',
    [
      {
        key: '1',
        label: 'List',
      },
      {
        key: '2',
        label: 'Calendar',
      },
    ],
  ],
])
const getViewHints = (view) => viewHintMap.get(view) ?? []
const renderViewHint = (items) => (
  <div className="flex gap-3 mt-1 font-sans text-xs opacity-90">
    {items.map((item) => (
      <span key={item.key} className="flex items-center gap-2">
        <Kbd>{item.key}</Kbd>
        {item.label}
      </span>
    ))}
  </div>
)
const PullToRefreshIndicator = ({ isMobile, pullProgress, isRefreshing }) => {
  if (!isMobile) return null
  return (
    <motion.div
      className="absolute top-0 left-0 right-0 flex justify-center py-4 pointer-events-none z-50"
      style={{
        opacity: pullProgress,
        y: pullProgress * 40 - 40,
      }}
    >
      <div
        className={cn(
          'bg-primary rounded-full p-2 shadow-lg transition-transform duration-300',
          isRefreshing && 'animate-spin',
        )}
      >
        <CheckCircle2 className="h-5 w-5 text-primary-foreground" />
      </div>
    </motion.div>
  )
}
const PullToRefreshHandle = ({ isMobile, handlePullDrag, handlePullDragEnd }) => {
  if (!isMobile) return null
  return (
    <motion.div
      drag="y"
      dragConstraints={{
        top: 0,
        bottom: 0,
      }}
      dragElastic={0.4}
      onDrag={handlePullDrag}
      onDragEnd={handlePullDragEnd}
      className="absolute top-0 left-0 right-0 h-8 z-40"
    />
  )
}
const TaskContentStack = ({
  loadContainer,
  loadItem,
  showCommandCenter,
  handleCreateStart,
  handleCommandUndo,
  handleCommandRedo,
  historySnap,
  handleToggleWeatherUnit,
  weatherUnit,
  handleToggleTimeFormat,
  timeFormat,
  showTime,
  showWeather,
  handleToggleTimeVisibility,
  handleToggleWeatherVisibility,
  handleToggleVisibility,
  taskViewProps,
}) => (
  <motion.div
    className="h-full flex flex-col relative"
    variants={loadContainer}
    initial={false}
    animate="show"
  >
    <CommandCenterContainer
      showCommandCenter={showCommandCenter}
      handleCreateStart={handleCreateStart}
      handleCommandUndo={handleCommandUndo}
      handleCommandRedo={handleCommandRedo}
      historySnap={historySnap}
      handleToggleWeatherUnit={handleToggleWeatherUnit}
      weatherUnit={weatherUnit}
      handleToggleTimeFormat={handleToggleTimeFormat}
      timeFormat={timeFormat}
      showTime={showTime}
      showWeather={showWeather}
      handleToggleTimeVisibility={handleToggleTimeVisibility}
      handleToggleWeatherVisibility={handleToggleWeatherVisibility}
      handleToggleVisibility={handleToggleVisibility}
      loadItem={loadItem}
    />
    <TaskViewSection {...taskViewProps} />
  </motion.div>
)
const MainContentSections = ({
  isRefreshing,
  taskViewProps,
  overlaysProps,
  loadContainer,
  loadItem,
  showCommandCenter,
  handleCreateStart,
  handleCommandUndo,
  handleCommandRedo,
  historySnap,
  handleToggleWeatherUnit,
  weatherUnit,
  handleToggleTimeFormat,
  timeFormat,
  showTime,
  showWeather,
  handleToggleTimeVisibility,
  handleToggleWeatherVisibility,
  handleToggleVisibility,
}) => (
  <div
    className={cn(
      'flex flex-col gap-2 w-full h-full transition-transform duration-200',
      isRefreshing && 'translate-y-12',
    )}
    id="main-task-container"
  >
    <TaskContentStack
      loadContainer={loadContainer}
      loadItem={loadItem}
      showCommandCenter={showCommandCenter}
      handleCreateStart={handleCreateStart}
      handleCommandUndo={handleCommandUndo}
      handleCommandRedo={handleCommandRedo}
      historySnap={historySnap}
      handleToggleWeatherUnit={handleToggleWeatherUnit}
      weatherUnit={weatherUnit}
      handleToggleTimeFormat={handleToggleTimeFormat}
      timeFormat={timeFormat}
      showTime={showTime}
      showWeather={showWeather}
      handleToggleTimeVisibility={handleToggleTimeVisibility}
      handleToggleWeatherVisibility={handleToggleWeatherVisibility}
      handleToggleVisibility={handleToggleVisibility}
      taskViewProps={taskViewProps}
    />
    <OverlaysSection {...overlaysProps} />
  </div>
)
const MainContentContainer = ({
  isMobile,
  pullProgress,
  isRefreshing,
  handlePullDrag,
  handlePullDragEnd,
  mainSectionsProps,
  ...props
}) => (
  <div className={cn('flex-1 min-w-0 flex flex-col relative')} {...props}>
    <PullToRefreshIndicator
      isMobile={isMobile}
      pullProgress={pullProgress}
      isRefreshing={isRefreshing}
    />
    <PullToRefreshHandle
      isMobile={isMobile}
      handlePullDrag={handlePullDrag}
      handlePullDragEnd={handlePullDragEnd}
    />
    <MainContentSections {...mainSectionsProps} />
  </div>
)
export function MainContent({
  isMobile,
  isTinyScreen,
  isResizing,
  handleToggleSidebar,
  todos,
  snap,
  historySnap,
  lists,
  selectedListId,
  setSelectedListId,
  listOps,
  selection,
  filtering,
  globalDrag,
  taskDrag,
  allStatuses,
  weatherUnit,
  timeFormat,

  handleToggleWeatherUnit,
  handleToggleTimeFormat,
  activeTab,
  setActiveTab,
}) {
  // Local State
  const searchInputRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const todoQuickAddRef = useRef(null)
  const doneQuickAddRef = useRef(null)
  const isAddTaskInteractionRef = useRef(false)

  // handleViewSelect uses setActiveTab which is now passed as prop
  const handleViewSelect = useCallback(
    (value) => {
      setActiveTab(value)
    },
    [setActiveTab],
  )

  // activeTab state removed (lifted to parent)
  // weatherUnit & timeFormat state removed - passed as props
  const [, setIsColorPickerOpen] = useState(false)
  const [newTaskId, setNewTaskId] = useState(null)
  const [editingId, setEditingIdState] = useState(null)
  const [isViewOpen, setIsViewOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  // Pull to refresh state
  const [pullProgress, setPullProgress] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Derived
  const anyExpanded = useMemo(() => todos.some((t) => t.isExpanded), [todos])
  const hasTodoTasks = useMemo(() => todos.some((t) => t.status === 'todo'), [todos])
  const hasDoneTasks = useMemo(() => todos.some((t) => t.status === 'done'), [todos])
  const { playDoneSound, playDeleteSound } = useSounds()

  // Destructure hooks for easier access
  const { selectedListIds, setSelectedListIds, bulkDeleteLists, bulkDuplicateLists } = listOps
  const {
    selectedIds,
    setSelectedIds,
    selectedIdsRef,
    isSelecting,
    setIsSelecting,
    recentlyDuplicatedIds,
    bulkDelete,
    bulkDuplicate,
    bulkComplete,
    handleItemSelect,
  } = selection
  const {
    sortBy,
    setSortBy,
    filterBy,
    setFilterBy,
    statusScope,
    setStatusScope,
    searchQuery,
    setSearchQuery,
    processedTodos,
    kanbanStatuses,
  } = filtering
  const {
    isGlobalDragging,
    isOverTask,
    droppedFiles,
    isTaskSelectorOpen,
    setIsTaskSelectorOpen,
    setDroppedFiles,
  } = globalDrag
  const {
    itemsByStatus,
    draggingIds,
    isDraggingTask,
    dragSizes,
    activeTodo,
    activeDragSize,
    isDragActive,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    resetDragState,
    registerItem,
    todoMap,
  } = taskDrag

  // Callbacks
  const editingIdRef = useRef(null)
  const setEditingId = useCallback(
    (nextId) => {
      const prevEditingId = editingIdRef.current
      if (prevEditingId && prevEditingId !== nextId) {
        const todo = todos.find((t) => t.id === prevEditingId)
        if (todo) {
          if (todo.text.trim().length === 0) {
            actions.deleteTodo(prevEditingId)
            if (prevEditingId !== newTaskId) {
              toast('Empty task removed', {
                icon: <Trash2 className="h-4 w-4 text-muted-foreground" />,
              })
            }
          } else if (prevEditingId === newTaskId && todo.text.trim().length >= 1) {
            toast('Task created', {
              icon: <CheckCircle2 className="h-4 w-4 text-primary" />,
              action: {
                label: (
                  <span className="flex items-center gap-2">
                    <Undo2 className="h-3 w-3" /> Undo
                  </span>
                ),
                onClick: () => {
                  if (undo())
                    toast('Undone', {
                      icon: <Undo2 className="h-4 w-4" />,
                    })
                },
              },
            })
          }
        }
        if (prevEditingId === newTaskId) {
          setNewTaskId(null)
        }
      }
      editingIdRef.current = nextId
      setEditingIdState(nextId)
    },
    [newTaskId, todos],
  )

  // Dismiss toasts
  useEffect(() => {
    if (selectedIds.size > 0) {
      toast.dismiss()
    }
  }, [selectedIds])
  const handleCreateStart = useCallback(
    (status) => {
      const newTodo = createTodo({
        listId: selectedListId === 'all' ? lists[0]?.id || 'personal' : selectedListId,
        completed: status === 'done',
        status: status,
      })
      const newId = newTodo.id
      actions.addTodo(newTodo)
      setEditingId(newId)
      setNewTaskId(newId)
      haptics.success()
    },
    [selectedListId, lists, setEditingId],
  )
  const toggleTodo = useCallback(
    (id) => {
      const selected = selectedIdsRef.current
      if (selected.has(id) && selected.size > 1) {
        const todo = store.todos.find((t) => t.id === id)
        if (todo) {
          const newCompleted = !todo.completed
          bulkComplete(newCompleted)
          return
        }
      }
      haptics.success()
      const todo = store.todos.find((t) => t.id === id)
      if (todo) {
        const newCompleted = !todo.completed
        if (newCompleted) playDoneSound()

        const updates = {
          completed: newCompleted,
        }

        // Sync status with completion state
        if (newCompleted) {
          updates.status = 'done'
        } else if (todo.status === 'done') {
          updates.status = 'todo'
        }

        actions.updateTodo(id, updates)
        toast(newCompleted ? 'Task marked done' : 'Task reopened', {
          icon: newCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-primary" />
          ) : (
            <RotateCcw className="h-4 w-4 text-primary" />
          ),
          action: {
            label: (
              <span className="flex items-center gap-2">
                <Undo2 className="h-3 w-3" /> Undo
              </span>
            ),
            onClick: () => {
              if (undo())
                toast('Undone', {
                  icon: <Undo2 className="h-4 w-4" />,
                })
            },
          },
        })
      }
    },
    [bulkComplete, playDoneSound, selectedIdsRef],
  )
  const handleQuickAdd = useCallback(
    (text, status, overrides) => {
      const { insertAfterId, index: insertIndex, subtasks, ...todoOverrides } = overrides || {}

      // Process subtasks from NLP
      const processedSubtasks =
        subtasks?.map((st) => (typeof st === 'string' ? createSubtask(st) : st)) || []

      const newTodo = createTodo({
        text,
        listId: selectedListId === 'all' ? lists[0]?.id || 'personal' : selectedListId,
        completed: status === 'done',
        status: status,
        subtasks: processedSubtasks,
        ...todoOverrides,
      })
      if (insertAfterId) {
        actions.insertTodoAfter(insertAfterId, newTodo)
      } else if (insertIndex !== undefined) {
        actions.insertTodoAt(insertIndex, newTodo)
      } else {
        actions.addTodo(newTodo)
      }
      haptics.success()
      toast('Task added', {
        icon: <CheckCircle2 className="h-4 w-4 text-primary" />,
      })
    },
    [selectedListId, lists],
  )
  const handleTaskDragCondition = useCallback(
    (e) => {
      const nativeEvent = e.inputEvent
      if (!isPrimaryPointerEvent(nativeEvent)) return false

      const target = nativeEvent?.target
      if (!(target instanceof Element)) return false
      if (isAddTaskInteractionRef.current || isAddTaskFocused() || isInsideAddTask(target))
        return false
      if (isDragBlockedTarget(target, isResizing, isDragActive)) return false
      return !isInteractiveTarget(target)
    },
    [isDragActive, isResizing],
  )
  const handleTaskSelectoStart = useCallback(
    (e) => {
      const nativeEvent = e.inputEvent
      if (!isPrimaryPointerEvent(nativeEvent)) {
        return
      }
      const target = nativeEvent?.target
      if (isAddTaskInteractionRef.current || isAddTaskFocused() || isInsideAddTask(target)) {
        setIsSelecting(false)
        if (selectedIdsRef.current.size > 0) {
          setSelectedIds(new Set())
        }
        return
      }

      if (isResizing || isDraggingTask || isGlobalDragging) {
        return
      }
      setIsSelecting(true)
      if (!nativeEvent.shiftKey && !nativeEvent.metaKey && !nativeEvent.ctrlKey) {
        setSelectedIds(new Set())
      }
    },
    [isDraggingTask, isGlobalDragging, isResizing, setIsSelecting, setSelectedIds, selectedIdsRef],
  )
  const handleTaskSelecto = useCallback(
    (e) => {
      if (isAddTaskInteractionRef.current || isAddTaskFocused()) return
      const added = e.added.map((el) => el.getAttribute('data-id')).filter(Boolean)
      const removed = e.removed.map((el) => el.getAttribute('data-id')).filter(Boolean)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        added.forEach((id) => next.add(id))
        removed.forEach((id) => next.delete(id))
        return next
      })
    },
    [setSelectedIds],
  )
  const handleTaskSelectoEnd = useCallback(() => {
    isAddTaskInteractionRef.current = false
    setIsSelecting(false)
  }, [setIsSelecting])

  const handleCommandUndo = useCallback(() => {
    undo()
  }, [])
  const handleCommandRedo = useCallback(() => {
    redo()
  }, [])
  const handleToggleTimeVisibility = useCallback(() => {
    actions.toggleUIVisibility('showTime')
  }, [])
  const handleToggleWeatherVisibility = useCallback(() => {
    actions.toggleUIVisibility('showWeather')
  }, [])
  const handleToggleVisibility = useCallback((e) => {
    const key = e.currentTarget.getAttribute('data-visibility-key')
    if (key && isUiVisibilityKey(key)) {
      actions.toggleUIVisibility(key)
    }
  }, [])
  const handleToggleSectionVisibility = useCallback((e) => {
    const status = e.currentTarget.getAttribute('data-status')
    if (status) {
      actions.toggleSectionVisibility(status)
    }
  }, [])
  const handleSortChange = useCallback(
    (value) => {
      if (value === 'date' || value === 'priority' || value === 'status') {
        setSortBy(value)
      } else {
        setSortBy(null)
      }
    },
    [setSortBy],
  )
  const handleFilterChange = useCallback(
    (value) => {
      if (value === 'overdue' || value === 'today' || value === 'tomorrow') {
        setFilterBy(value)
      } else {
        setFilterBy(null)
      }
    },
    [setFilterBy],
  )
  const handleClearSort = useCallback(() => {
    setSortBy(null)
  }, [setSortBy])
  const handleClearFilter = useCallback(() => {
    setFilterBy(null)
  }, [setFilterBy])
  const handleToggleAllExpanded = useCallback(() => {
    if (anyExpanded) {
      actions.setAllExpanded(false)
    }
  }, [anyExpanded])
  const handleToggleAllDone = useCallback(() => {
    const shouldMarkDone = hasTodoTasks
    if (shouldMarkDone) {
      actions.markAllAsDone()
      playDoneSound()
    } else {
      actions.markAllAsTodo()
    }
  }, [hasTodoTasks, playDoneSound])
  const deleteTodo = useCallback(
    (id) => {
      actions.deleteTodo(id)
      playDeleteSound()
      showUndoToast('Task deleted', <Trash2 className="h-4 w-4 text-destructive" />)
    },
    [playDeleteSound],
  )
  const updateTodo = useCallback((id, updates) => {
    actions.updateTodo(id, updates)
  }, [])
  const duplicateTodo = useCallback((id) => {
    actions.duplicateTodo(id)
    haptics.success()
    toast('Task duplicated', {
      icon: <CheckCircle2 className="h-4 w-4 text-primary" />,
    })
  }, [])
  const handleBulkCompleteClick = useCallback(() => {
    const todosInSelection = todos.filter((t) => selectedIds.has(t.id))
    const allDone = todosInSelection.every((t) => t.status === 'done')
    bulkComplete(!allDone)
  }, [bulkComplete, selectedIds, todos])
  const handleClearSelectedIds = useCallback(() => {
    setSelectedIds(new Set())
  }, [setSelectedIds])
  const isClickOnInteractiveElement = useCallback((target) => {
    if (!(target instanceof Element)) {
      return false
    }
    return (
      target.closest('.task-item') ||
      target.closest('.sidebar-list-item') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('.selection-area') ||
      target.closest('[role="menuitem"]') ||
      target.closest('[role="combobox"]') ||
      target.closest('[role="option"]') ||
      target.closest('[role="dialog"]') ||
      target.closest('[role="popover"]') ||
      target.closest('[data-radix-portal]') ||
      target.closest('[data-radix-popper-content-wrapper]') ||
      target.closest(editableContentSelector) ||
      target.closest('.task-detail-panel') ||
      target.closest('.cursor-col-resize')
    )
  }, [])

  const handleMainMouseDown = useCallback(
    (e) => {
      const target = e.target
      if (isInsideAddTask(target)) {
        isAddTaskInteractionRef.current = true
        if (selectedIds.size > 0) {
          setSelectedIds(new Set())
        }
        if (selectedListIds.size > 0) {
          setSelectedListIds(new Set())
        }
        return
      }
      isAddTaskInteractionRef.current = false

      // Don't clear selection if clicking on interactive elements
      if (isClickOnInteractiveElement(target)) {
        return
      }

      // Clear expanded state if clicking outside
      if (anyExpanded) {
        actions.setAllExpanded(false)
      }

      // Clear selection if clicking outside
      if (selectedIds.size > 0) {
        setSelectedIds(new Set())
      }
    },
    [
      selectedIds.size,
      setSelectedIds,
      selectedListIds.size,
      setSelectedListIds,
      anyExpanded,
      isClickOnInteractiveElement,
    ],
  )
  const handleAddTaskFocusIn = useCallback(
    (event) => {
      const target = event.target
      if (!isInsideAddTask(target)) {
        return
      }
      isAddTaskInteractionRef.current = true
      if (selectedIds.size > 0) {
        setSelectedIds(new Set())
      }
      if (selectedListIds.size > 0) {
        setSelectedListIds(new Set())
      }
    },
    [selectedIds.size, setSelectedIds, selectedListIds.size, setSelectedListIds],
  )
  useEffect(() => {
    if (!isAddTaskInteractionRef.current && !isAddTaskFocused()) return
    if (selectedIds.size > 0) {
      setSelectedIds(new Set())
    }
    if (selectedListIds.size > 0) {
      setSelectedListIds(new Set())
    }
  }, [selectedIds.size, selectedListIds.size, setSelectedIds, setSelectedListIds])
  useEffect(() => {
    const handlePointerDown = (event) => {
      handleMainMouseDown(event)
    }
    const handlePointerUp = () => {
      isAddTaskInteractionRef.current = isAddTaskFocused()
    }
    const handleFocusIn = (event) => {
      handleAddTaskFocusIn(event)
    }
    // Attach to document to catch clicks anywhere in the app
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('pointerup', handlePointerUp)
    document.addEventListener('focusin', handleFocusIn)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('pointerup', handlePointerUp)
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [handleMainMouseDown, handleAddTaskFocusIn])

  const handleMobileNavSelect = useCallback((view) => {
    setActiveTab(view)
  }, [])
  const handleAttachTodoSelect = useCallback(
    (val) => {
      const todoId = val
      const todo = todos.find((t) => t.id === todoId)
      if (todo) {
        actions.attachFiles(todoId, droppedFiles)
        toast(`Attached ${droppedFiles.length} files to "${todo.text}"`)
        setDroppedFiles([])
        setIsTaskSelectorOpen(false)
      }
    },
    [droppedFiles, todos, setDroppedFiles, setIsTaskSelectorOpen],
  )
  const handleCancelAttach = useCallback(() => {
    setDroppedFiles([])
    setIsTaskSelectorOpen(false)
  }, [setDroppedFiles, setIsTaskSelectorOpen])
  const statusCreateHandlers = useMemo(() => {
    return new Map(allStatuses.map((st) => [st, () => handleCreateStart(st)]))
  }, [allStatuses, handleCreateStart])
  const statusColorHandlers = useMemo(() => {
    return new Map(allStatuses.map((st) => [st, (color) => actions.updateSectionColor(st, color)]))
  }, [allStatuses])
  const statusQuickAddHandlers = useMemo(() => {
    return new Map(
      allStatuses.map((st) => [st, (text, overrides) => handleQuickAdd(text, st, overrides)]),
    )
  }, [allStatuses, handleQuickAdd])
  const handlePullDrag = useCallback(
    (_e, info) => {
      if (isRefreshing || !isMobile) return
      const progress = Math.min(info.offset.y / 100, 1)
      if (progress > 0) {
        setPullProgress(progress)
        if (progress === 1 && pullProgress < 1) {
          haptics.impact()
        }
      }
    },
    [isRefreshing, isMobile, pullProgress],
  )
  const handlePullDragEnd = useCallback(
    (_e, info) => {
      if (isRefreshing || !isMobile) return
      if (info.offset.y > 100) {
        setIsRefreshing(true)
        setPullProgress(1)
        haptics.success()
        // Simulate refresh
        setTimeout(() => {
          setIsRefreshing(false)
          setPullProgress(0)
          toast.success('Sync complete', {
            description: 'Your tasks are up to date.',
          })
        }, 1500)
      } else {
        setPullProgress(0)
      }
    },
    [isRefreshing, isMobile],
  )

  // Shortcuts Effect
  useEffect(() => {
    const closeAllMenus = () => {
      setIsViewOpen(false)
      setIsFilterOpen(false)
      setNewTaskId(null)
      setIsTaskSelectorOpen(false)
    }

    const navigateTasks = (direction) => {
      const taskElements = Array.from(
        document.querySelectorAll('.task-item[data-id], .task-item[data-todo-id]'),
      )
      if (taskElements.length === 0) return

      const activeElement = document.activeElement
      const currentIndex = taskElements.findIndex(
        (el) => el === activeElement || el.contains(activeElement),
      )

      let nextIndex
      if (currentIndex === -1) {
        nextIndex = direction > 0 ? 0 : taskElements.length - 1
      } else {
        nextIndex = (currentIndex + direction + taskElements.length) % taskElements.length
      }

      const nextEl = taskElements[nextIndex]
      if (nextEl instanceof HTMLElement) {
        nextEl.focus()
        nextEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }

    const toggleSelectCurrent = () => {
      const activeEl = document.activeElement
      const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
      const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
      if (id) {
        handleItemSelect(id, true)
      }
    }

    const completeCurrent = () => {
      const activeEl = document.activeElement
      const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
      const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
      if (id) {
        toggleTodo(id)
      }
    }

    const TOAST_ID = 'view-status-toast'
    const handleModifierShortcuts = (event) => {
      if (!event.metaKey && !event.ctrlKey) return false
      const key = event.key.toLowerCase()
      switch (key) {
        case 'k':
          actions.toggleUIVisibility('showCommandCenter')
          break
        case 'z':
          event.shiftKey ? redo() : undo()
          break
        case 'b':
          actions.toggleUIVisibility('showSidebar')
          break
        case 'e':
          if (selectedIds.size === 1) {
            const id = Array.from(selectedIds)[0]
            const todo = store.todos.find((t) => t.id === id)
            if (todo) {
              actions.updateTodo(id, { isExpanded: !todo.isExpanded })
            }
            break
          }
          if (anyExpanded) {
            actions.setAllExpanded(false)
          }
          break
        case 'd':
          if (selectedIds.size > 0) {
            bulkDuplicate()
            break
          }
          if (hasTodoTasks) {
            actions.markAllAsDone()
            toast.success('All tasks marked as done')
          }
          break
        case 't':
          if (!hasTodoTasks) {
            actions.markAllAsTodo()
            toast.success('All tasks marked as todo')
          }
          break
        default:
          return false
      }
      event.preventDefault()
      return true
    }
    const handleViewSwitch = (key) => {
      let nextView = null
      switch (key) {
        case '1':
          nextView = 'list'
          break
        case '2':
          nextView = 'calendar'
          break
        case '3':
          nextView = 'kanban'
          break
        default:
          break
      }
      if (!nextView) return false
      closeAllMenus()
      if (activeTab === nextView) {
        const label = nextView.charAt(0).toUpperCase() + nextView.slice(1)
        toast.message(`Already in ${label} view`, {
          id: TOAST_ID,
          description: renderViewHint(getViewHints(nextView)),
          duration: 2000,
        })
        return true
      }
      toast.dismiss(TOAST_ID)
      setActiveTab(nextView)
      return true
    }
    const handlePanelToggle = (key, event) => {
      if (key !== 's' && key !== 'f' && key !== 'v') return false
      event.preventDefault()
      event.stopPropagation()
      const wasOpen = key === 'f' ? isFilterOpen : isViewOpen
      closeAllMenus()
      if (!wasOpen) {
        const openFn = key === 'f' ? setIsFilterOpen : setIsViewOpen
        setTimeout(() => openFn(true), 10)
      }
      return true
    }
    const singleKeyHandlers = new Map([
      [
        'm',
        () => {
          if (selectedIds.size !== 0) return
          closeAllMenus()
          actions.toggleUIVisibility('showSidebar')
        },
      ],
      [
        't',
        () => {
          if (selectedIds.size !== 0) return
          closeAllMenus()
          if (todoQuickAddRef.current) todoQuickAddRef.current.focus()
          else handleCreateStart('todo')
        },
      ],
      [
        'n',
        () => {
          if (selectedIds.size !== 0) return
          closeAllMenus()
          if (todoQuickAddRef.current) todoQuickAddRef.current.focus()
          else handleCreateStart('todo')
        },
      ],
      [
        'c',
        () => {
          if (selectedIds.size !== 0) return
          closeAllMenus()
          if (todoQuickAddRef.current) todoQuickAddRef.current.focus()
          else handleCreateStart('todo')
        },
      ],
      [
        'd',
        () => {
          if (selectedIds.size !== 0) return
          closeAllMenus()
          if (doneQuickAddRef.current) doneQuickAddRef.current.focus()
          else handleCreateStart('done')
        },
      ],
      [
        'delete',
        () => {
          if (selectedIds.size === 0) {
            const activeEl = document.activeElement
            const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
            const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
            if (id) {
              deleteTodo(id)
              return
            }
          }
          closeAllMenus()
          bulkDelete()
        },
      ],
      [
        'backspace',
        () => {
          if (selectedIds.size === 0) {
            const activeEl = document.activeElement
            const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
            const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
            if (id) {
              deleteTodo(id)
              return
            }
          }
          closeAllMenus()
          bulkDelete()
        },
      ],
      [
        '#',
        () => {
          if (selectedIds.size === 0) {
            const activeEl = document.activeElement
            const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
            const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
            if (id) {
              deleteTodo(id)
              return
            }
          }
          closeAllMenus()
          bulkDelete()
        },
      ],
      [
        'escape',
        () => {
          closeAllMenus()
          if (selectedIds.size > 0) setSelectedIds(new Set())
          if (selectedListIds.size > 0) setSelectedListIds(new Set())
        },
      ],
      [
        '/',
        () => {
          closeAllMenus()
          searchInputRef.current?.focus()
        },
      ],
      ['j', () => navigateTasks(1)],
      ['k', () => navigateTasks(-1)],
      ['x', () => toggleSelectCurrent()],
      ['e', () => completeCurrent()],
      ['a', () => (selectedIds.size > 0 ? bulkComplete(true) : completeCurrent())],
      [
        'o',
        () => {
          const activeEl = document.activeElement
          const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
          const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
          if (id) actions.updateTodo(id, { isExpanded: true })
        },
      ],
      [
        'enter',
        () => {
          const activeEl = document.activeElement
          const taskEl = activeEl?.closest('[data-id], [data-todo-id]')
          const id = taskEl?.getAttribute('data-id') || taskEl?.getAttribute('data-todo-id')
          if (id) actions.updateTodo(id, { isExpanded: true })
        },
      ],
    ])
    const handleSingleKeyAction = (key, event) => {
      const handler = singleKeyHandlers.get(key)
      if (!handler) return false
      event.preventDefault()
      event.stopPropagation()
      handler()
      return true
    }
    const handleShortcuts = (event) => {
      if (isEditableTarget(event.target)) {
        if (event.key === 'Escape') {
          event.target.blur()
        }
        return
      }

      const key = event.key.toLowerCase()

      // Global shortcuts that work even if a popup is open (like the ones user mentioned)
      const isGlobalShortcut = [
        '/',
        'f',
        's',
        'm',
        't',
        'd',
        '1',
        '2',
        '3',
        'j',
        'k',
        'x',
        'e',
        'c',
        'v',
        'a',
        'n',
        'o',
        '#',
      ].includes(key)

      // We don't block j, k, x, e, etc. on tasks themselves even though they are buttons
      const isTaskTarget = event.target.closest('.task-item')
      const isBlocked = isShortcutBlockedTarget(event.target) && !isGlobalShortcut && !isTaskTarget

      if (isBlocked) {
        return
      }

      if (handleModifierShortcuts(event)) return
      if (handleViewSwitch(key)) return
      if (handlePanelToggle(key, event)) return
      handleSingleKeyAction(key, event)
    }
    window.addEventListener('keydown', handleShortcuts, true)
    return () => window.removeEventListener('keydown', handleShortcuts, true)
  }, [
    activeTab,
    setActiveTab,
    handleCreateStart,
    bulkDuplicate,
    bulkDelete,
    bulkComplete,
    toggleTodo,
    deleteTodo,
    handleItemSelect,
    hasTodoTasks,
    selectedIds,
    selectedListIds,
    setSelectedIds,
    setSelectedListIds,
    setEditingId,
    setIsTaskSelectorOpen,
    isViewOpen,
    isFilterOpen,
    anyExpanded,
  ])
  const handleClearSelectedListIds = useCallback(() => {
    setSelectedListIds(new Set())
  }, [setSelectedListIds])

  // Variants (copy from index.tsx)
  const loadContainer = {
    hidden: {
      opacity: 0,
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.05,
      },
    },
  }
  const loadItem = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: easeOutQuint,
      },
    },
  }
  const taskViewProps = {
    todos,
    lists,
    isMobile,
    isTinyScreen,
    activeTab,
    handleViewSelect,
    processedTodos,
    searchQuery,
    setSearchQuery,
    kanbanStatuses,
    allStatuses,
    itemsByStatus,
    todoMap,
    uiSettings: snap.uiSettings,
    sensors,
    collisionDetection,
    isDragActive,
    draggingIds,
    dragSizes,
    activeTodo,
    activeDragSize,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    resetDragState,
    registerItem,
    statusCreateHandlers,
    statusColorHandlers,
    statusQuickAddHandlers,
    toggleTodo,
    deleteTodo,
    updateTodo,
    handleItemSelect,
    duplicateTodo,
    setEditingId,
    setIsColorPickerOpen,
    selectedIds,
    editingId,
    recentlyDuplicatedIds,
    newTaskId,
    isSelecting,
    selectedListId,
    sectionColors: snap.sectionColors,
    todoQuickAddRef,
    doneQuickAddRef,
    searchInputRef,
    sortBy,
    handleSortChange,
    handleClearSort,
    filterBy,
    handleFilterChange,
    handleClearFilter,
    statusScope,
    setStatusScope,
    handleToggleAllExpanded,
    handleToggleAllDone,
    anyExpanded,
    hasTodoTasks,
    hasDoneTasks,
    todosLength: todos.length,
    handleToggleVisibility,
    handleToggleSectionVisibility,
    handleTaskDragCondition,
    handleTaskSelectoStart,
    handleTaskSelecto,
    handleTaskSelectoEnd,
    handleCreateStart,
    handleQuickAdd,
    isViewOpen,
    setIsViewOpen,
    isFilterOpen,
    setIsFilterOpen,
    handleToggleSidebar,
    showSidebar: snap.uiSettings.showSidebar,
    loadItem,
    scrollContainerRef,
    handleClearSelectedIds,
    setSelectedListId,
    weatherUnit,
  }
  const overlaysProps = {
    isMobile,
    isGlobalDragging,
    isOverTask,
    isTaskSelectorOpen,
    setIsTaskSelectorOpen,
    droppedFiles,
    todos,
    handleAttachTodoSelect,
    handleCancelAttach,
    activeTab,
    handleMobileNavSelect,
    selectedIds,
    handleBulkCompleteClick,
    bulkDuplicate,
    bulkDelete,
    handleClearSelectedIds,
    selectedListIds,
    bulkDuplicateLists,
    bulkDeleteLists,
    handleClearSelectedListIds,
    handleToggleSidebar,
  }
  const mainSectionsProps = {
    isRefreshing,
    taskViewProps,
    overlaysProps,
    loadContainer,
    loadItem,
    showCommandCenter: snap.uiSettings.showCommandCenter,
    handleCreateStart,
    handleCommandUndo,
    handleCommandRedo,
    historySnap,
    handleToggleWeatherUnit,
    weatherUnit,
    handleToggleTimeFormat,
    timeFormat,
    showTime: snap.uiSettings.showTime,
    showWeather: snap.uiSettings.showWeather,
    handleToggleTimeVisibility,
    handleToggleWeatherVisibility,
    handleToggleVisibility,
    showShortcuts: snap.uiSettings.showShortcuts,
  }
  return (
    <ContextMenu onOpenChange={actions.setContextMenuOpen}>
      <ContextMenuTrigger asChild>
        <MainContentContainer
          isMobile={isMobile}
          pullProgress={pullProgress}
          isRefreshing={isRefreshing}
          handlePullDrag={handlePullDrag}
          handlePullDragEnd={handlePullDragEnd}
          mainSectionsProps={mainSectionsProps}
        />
      </ContextMenuTrigger>
      <GlobalContextMenuContent
        handleToggleSidebar={handleToggleSidebar}
        showSidebar={snap.uiSettings.showSidebar}
        handleToggleAllExpanded={handleToggleAllExpanded}
        anyExpanded={anyExpanded}
        handleToggleAllDone={handleToggleAllDone}
        hasTodoTasks={hasTodoTasks}
        hasDoneTasks={hasDoneTasks}
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        handleClearSort={handleClearSort}
        filterBy={filterBy}
        handleFilterChange={handleFilterChange}
        handleClearFilter={handleClearFilter}
      >
        <ContextMenuItem onSelect={() => handleCreateStart('todo')}>
          <Plus className="mr-2 h-4 w-4" />
          <span>Add task</span>
          <KbdGroup className="ml-auto">
            <Kbd>N</Kbd>
          </KbdGroup>
        </ContextMenuItem>
      </GlobalContextMenuContent>
    </ContextMenu>
  )
}
function CommandCenterContainer(props) {
  return (
    <div className="container mx-auto px-4 relative max-w-2xl">
      <CommandCenterSection {...props} />
    </div>
  )
}
